import { useEffect, useState } from 'react';
import { getToken } from '../lib/api';
import { getLang, useT } from '../lib/i18n';
import { alert, haptic } from '../lib/telegram';
import { COACH_TZ } from '../lib/time';
import { PinnedSheet } from './SetSheet';

/**
 * Деньги → «Счёт за корт» (server: routes/court-bill.ts): the sessions that
 * used a court in a week or a month, each with its rate and cost, and the
 * totals by rate — the same numbers as the Money tab. «Отправить себе в
 * Telegram» sends it as plain text, to forward to the club. «Копировать для
 * таблицы» and «Прислать файл в чат» give the same weeks in the club's
 * spreadsheet layout (server: coach/court-export.ts).
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }), authorization: `Bearer ${getToken()}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error ?? `http_${res.status}`); }
  return res.json();
}

interface Bill {
  from: string; to: string;
  sessions: { date: string; start: string; end: string; minutes: number; noShow: boolean; parts: { name: string; minutes: number; rate: number; cost: number }[]; cost: number }[];
  groups: { name: string; rate: number; minutes: number; cost: number }[];
  minutes: number; total: number;
}

/** The club's spreadsheet rows for the same weeks (GET /api/coach/court-bill/export). */
interface Export {
  tsv: string; filename: string; total: number; end: number;
  warning: { base: number[]; band: number[] } | null;
}

/** «Остаток на начало недели», remembered on this device. */
const CARRY_KEY = 'cb.carry';
const savedCarry = () => { try { return localStorage.getItem(CARRY_KEY) ?? ''; } catch { return ''; } };
/** '12,5' or '12.5'; empty is 0; anything else is null. */
const carryValue = (s: string) => { const v = s.trim() === '' ? 0 : Number(s.trim().replace(',', '.')); return Number.isFinite(v) ? v : null; };

/**
 * Copies inside the tap itself — iOS forgets the tap once an await has
 * passed, so the rows are fetched beforehand. Falls back to a selected
 * text field where the clipboard API is refused.
 */
async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* below */ }
  const ta = document.createElement('textarea');
  ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  let done = false;
  try { done = document.execCommand('copy'); } catch { done = false; }
  ta.remove();
  return done;
}

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (s: string, n: number) => ymd(new Date(Date.parse(`${s}T12:00:00Z`) + n * 86400000));
/** Today in the coach's time zone. */
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: COACH_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
/** The Monday of the week holding `s`. */
const monday = (s: string) => addDays(s, -((new Date(`${s}T12:00:00Z`).getUTCDay() + 6) % 7));
const monthEnd = (s: string) => { const d = new Date(`${s.slice(0, 7)}-01T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + 1); d.setUTCDate(0); return ymd(d); };
const shiftMonth = (s: string, n: number) => { const d = new Date(`${s.slice(0, 7)}-01T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return ymd(d); };

type Mode = 'week' | 'month';
/** The last full week (Monday to Sunday) or the last full month. */
const initial = (mode: Mode) => mode === 'week'
  ? { from: addDays(monday(today()), -7), to: addDays(monday(today()), -1) }
  : (() => { const f = shiftMonth(today(), -1); return { from: f, to: monthEnd(f) }; })();

const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="8" width="12" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h2" />
  </svg>
);
const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H6v18h12V7z" /><path d="M14 3v4h4M9 13h6M9 17h6" />
  </svg>
);

export function CourtBill({ onClose }: { onClose: () => void }) {
  const t = useT();
  const lang = getLang() === 'en' ? 'en' : 'ru';
  const loc = lang === 'ru' ? 'ru-RU' : 'en-GB';
  const [mode, setMode] = useState<Mode>('week');
  const [period, setPeriod] = useState(() => initial('week'));
  const [bill, setBill] = useState<Bill | null>(null);
  const [busy, setBusy] = useState(false);
  const [carry, setCarry] = useState(savedCarry);
  const [rows, setRows] = useState<Export | null>(null);
  useEffect(() => {
    setBill(null);
    api<Bill>(`/api/coach/court-bill?from=${period.from}&to=${period.to}`).then(setBill).catch(() => setBill({ from: period.from, to: period.to, sessions: [], groups: [], minutes: 0, total: 0 }));
  }, [period.from, period.to]);
  // The spreadsheet rows, ready before the tap (copyText).
  const start = carryValue(carry);
  useEffect(() => {
    setRows(null);
    if (start === null) return;
    let live = true;
    const id = setTimeout(() => {
      api<Export>(`/api/coach/court-bill/export?from=${period.from}&to=${period.to}&carry=${start}`)
        .then((x) => { if (live) setRows(x); }).catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(id); };
  }, [period.from, period.to, start]);
  const editCarry = (v: string) => {
    setCarry(v);
    try { localStorage.setItem(CARRY_KEY, v); } catch { /* this device only */ }
  };
  const rateList = (w: NonNullable<Export['warning']>) => [...w.base, ...w.band].map((n) => money(n)).join(', ');

  const fmt = (s: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(loc, { timeZone: 'UTC', ...o }).format(new Date(`${s}T12:00:00Z`));
  const label = mode === 'week'
    ? t('cb.week', { range: period.from.slice(0, 7) === period.to.slice(0, 7)
        ? `${fmt(period.from, { day: 'numeric' })}–${fmt(period.to, { day: 'numeric', month: 'long' })}`
        : `${fmt(period.from, { day: 'numeric', month: 'short' })} – ${fmt(period.to, { day: 'numeric', month: 'short' })}` })
    : (() => { const m = fmt(period.from, { month: 'long', year: 'numeric' }).replace(/ г\.$/, ''); return m.charAt(0).toUpperCase() + m.slice(1); })();
  const step = (n: number) => {
    haptic.tap();
    setPeriod(mode === 'week'
      ? { from: addDays(period.from, 7 * n), to: addDays(period.to, 7 * n) }
      : (() => { const f = shiftMonth(period.from, n); return { from: f, to: monthEnd(f) }; })());
  };
  const money = (n: number) => {
    const v = Number.isInteger(n) ? String(n) : n.toFixed(2);
    return lang === 'ru' ? `${v.replace('.', ',')} €` : `€${v}`;
  };
  const dur = (m: number) => (m < 60 ? t('cb.min', { n: m }) : t('cb.hours', { n: lang === 'ru' ? String(Math.round(m / 6) / 10).replace('.', ',') : String(Math.round(m / 6) / 10) }));

  return (
    <PinnedSheet full title={t('cb.title')} onClose={onClose}
      footer={<button className="savebtn" disabled={busy || !bill?.sessions.length} onClick={async () => {
        setBusy(true);
        try {
          await api('/api/coach/court-bill/send', { method: 'POST', body: JSON.stringify({ from: period.from, to: period.to }) });
          haptic.success();
          await alert(t('cb.sent'));
        } catch (e: any) { haptic.error(); await alert(t('co.failed', { message: e.message })); } finally { setBusy(false); }
      }}>{t('cb.send')}</button>}>
      <div className="cb-modes" role="tablist">
        {(['week', 'month'] as const).map((m) => (
          <button key={m} role="tab" className="chip" aria-pressed={mode === m}
            onClick={() => { haptic.tap(); setMode(m); setPeriod(initial(m)); }}>{t(m === 'week' ? 'cb.byWeek' : 'cb.byMonth')}</button>
        ))}
      </div>
      <div className="cb-period">
        <button className="cb-step" aria-label="‹" onClick={() => step(-1)}>‹</button>
        <b>{label}</b>
        <button className="cb-step" aria-label="›" onClick={() => step(1)}>›</button>
      </div>
      {!bill ? <div className="skeleton" /> : bill.sessions.length === 0 ? (
        <div className="card__when cb-none">{t('cb.none')}</div>
      ) : (
        <>
          <div className="cb-list">
            {bill.sessions.map((s, i) => (
              <div key={i} className="cb-row">
                <span className="cb-row__when">
                  <b>{fmt(s.date, { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '')}</b>
                  <small>{s.start}–{s.end} · {dur(s.minutes)}{s.noShow ? ` · ${t('cb.noShow')}` : ''}</small>
                </span>
                <span className="cb-row__rate">{s.parts.map((p) => (s.parts.length > 1 ? `${p.name} ${dur(p.minutes)}` : p.name)).join(' + ')}</span>
                <b className="cb-row__cost">{money(s.cost)}</b>
              </div>
            ))}
          </div>
          <div className="cb-total">
            <div className="cb-total__line"><span>{t('cb.onCourt')}</span><b>{dur(bill.minutes)}</b></div>
            {bill.groups.map((g) => (
              <div key={`${g.name}${g.rate}`} className="cb-total__line cb-total__line--soft">
                <span>{g.name} {dur(g.minutes)} × {money(g.rate)}</span><span>{money(g.cost)}</span>
              </div>
            ))}
            <div className="cb-total__line cb-total__line--pay"><span>{t('cb.toPay')}</span><b>{money(bill.total)}</b></div>
          </div>
        </>
      )}
      <div className="cb-export">
        <label className="cb-carry">
          <span>{t('cb.carry')}</span>
          <input inputMode="decimal" value={carry} placeholder="0" aria-invalid={start === null}
            onChange={(e) => editCarry(e.target.value)} />
        </label>
        {rows?.warning && <div className="warnline">{t('cb.rateWarn', { rates: rateList(rows.warning) })}</div>}
        <button className="cb-btn" disabled={!rows} onClick={async () => {
          if (!rows) return;
          haptic.tap();
          if (await copyText(rows.tsv)) { haptic.success(); await alert(t('cb.copied')); }
          else { haptic.error(); await alert(t('co.failed', { message: 'clipboard' })); }
        }}>
          <CopyIcon />
          <span><b>{t('cb.copy')}</b><small>{t('cb.copyHint')}</small></span>
        </button>
        <button className="cb-btn" disabled={!rows || busy} onClick={async () => {
          if (!rows || start === null) return;
          setBusy(true);
          try {
            await api('/api/coach/court-bill/file', { method: 'POST', body: JSON.stringify({ from: period.from, to: period.to, carry: start }) });
            haptic.success();
            await alert(t('cb.fileSent', { name: rows.filename }));
          } catch (e: any) { haptic.error(); await alert(t('co.failed', { message: e.message })); } finally { setBusy(false); }
        }}>
          <FileIcon />
          <span><b>{t('cb.file')}</b><small>{t('cb.fileHint', { name: rows?.filename ?? 'court-week.csv' })}</small></span>
        </button>
      </div>
    </PinnedSheet>
  );
}
